<template>
  <div id="element-to-print" class="certificate-container-print">
    <!-- Watermark component (shown when showWatermark is true) -->
    <Watermark v-if="props.certificateData.showWatermark" />

    <!-- Certificate Content -->
    <div class="certificate-content-wrapper">
      <!-- Certificate content with fixed size -->
      <div class="certificate-fixed">
        <img :src="bgImgSrc" class="certificate-image" />
        <!-- Company Name -->
        <div
          class="cert-field company-name"
          :style="{
            top: `${layout.companyName.top}px`,
            left:
              typeof layout.companyName.left === 'number'
                ? `${layout.companyName.left}px`
                : layout.companyName.left,
            transform: typeof layout.companyName.left === 'number' ? 'none' : 'translateX(-50%)',
            fontSize: `${layout.companyName.fontSize}px`,
            fontWeight: layout.companyName.fontWeight,
            width: layout.companyName.width
              ? typeof layout.companyName.width === 'number'
                ? `${layout.companyName.width}px`
                : layout.companyName.width
              : 'auto',
            maxWidth: layout.companyName.maxWidth ? `${layout.companyName.maxWidth}px` : 'none',
          }"
        >
          {{ props.certificateData.companyName }}
        </div>

        <!-- Valuation Result -->
        <div
          class="cert-field valuation-result"
          :style="{
            top:
              typeof layout.valuationResult.top === 'number'
                ? `${layout.valuationResult.top}px`
                : layout.valuationResult.top,
            left:
              typeof layout.valuationResult.left === 'number'
                ? `${layout.valuationResult.left}px`
                : layout.valuationResult.left,
            transform: 'translate(-50%, -50%)',
            fontSize: `${layout.valuationResult.fontSize}px`,
            fontWeight: layout.valuationResult.fontWeight,
          }"
        >
          {{ props.certificateData.valuationLower }} {{ props.certificateData.currency }} ~
          {{ props.certificateData.valuationUpper }} {{ props.certificateData.currency }}
        </div>

        <!-- Date -->
        <div
          class="cert-field date"
          :style="{
            bottom: `${layout.date.bottom}px`,
            left: `${layout.date.left}px`,
            fontSize: `${layout.date.fontSize}px`,
          }"
        >
          {{ props.certificateData.date }}
        </div>

        <!-- Certificate Number -->
        <div
          class="cert-field certificate-number"
          :style="{
            bottom: `${layout.certificateNumber.bottom}px`,
            left: `${layout.certificateNumber.left}px`,
            fontSize: `${layout.certificateNumber.fontSize}px`,
          }"
        >
          {{ props.certificateData.certificateNumber }}
        </div>

        <!-- Consultant Signature -->
        <div
          class="cert-field signature"
          :style="{
            bottom: `${layout.signature.bottom}px`,
            left:
              typeof layout.signature.left === 'number'
                ? `${layout.signature.left}px`
                : layout.signature.left,
            transform: typeof layout.signature.left === 'number' ? 'none' : 'translateX(-50%)',
            fontSize: `${layout.signature.fontSize}px`,
            fontFamily: layout.signature.fontFamily,
          }"
        >
          {{ props.certificateData.consultantName }}
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, onMounted } from "vue";
import Watermark from "@/components/Watermark.vue";
import { getCertificateLayout, type CertificateLayout } from "./certificateLayout";

// Certificate data interface
export interface CertificateData {
  companyName: string;
  valuationLower: string;
  valuationUpper: string;
  currency: string;
  date: string;
  certificateNumber: string;
  consultantName: string;
  companyType: number; // 1=general, 2=innovation, 8=patent, 9=bank
  showWatermark: boolean;
}

// Define props
const props = defineProps<{
  certificateData: CertificateData;
}>();

// Computed property to determine background class based on company type
const bgImgSrc = ref<string>("");
onMounted(() => {
  const type = props.certificateData.companyType;
  if (type === 1 || type === 2) {
    bgImgSrc.value = "/images/Certificate_EN_General.png";
  } else if (type === 8) {
    bgImgSrc.value = "/images/Certificate_EN_Patent.png";
  } else if (type === 9) {
    bgImgSrc.value = "/images/Certificate_EN_BankLoan.png";
  }
});

// Computed property to get layout configuration
const layout = computed<CertificateLayout>(() => {
  return getCertificateLayout(props.certificateData.companyType);
});
</script>

<style scoped>
/* Certificate container */
* {
  box-sizing: border-box;
}

.certificate-container-print {
  margin: 0;
  padding: 0;
  position: relative;
  max-width: unset !important;
  width: 1785px !important; /* Image width */
  height: 2526px !important;
  box-sizing: border-box;
  background: transparent;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  margin: 0 auto;
}

.certificate-content-wrapper {
  padding: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  overflow: hidden;
}

/* Fixed certificate size based on image dimensions: 1785px x 2526px */
.certificate-fixed {
  position: relative;
  width: 100%;
  height: 100%;
}
.certificate-image {
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* Certificate fields - using absolute positioning with dynamic styles */
.cert-field {
  position: absolute;
  font-family: Arial, sans-serif;
  color: #000;
  line-height: 1.2;
}

.company-name {
  text-align: center;
}

.valuation-result {
  text-align: center;
  white-space: nowrap;
}

.date {
  text-align: right;
}

.certificate-number {
  text-align: right;
}

.signature {
  text-align: center;
  white-space: nowrap;
  font-weight: 600;
}
</style>
